<template>
  <div
    class="flex grow flex-col overflow-y-auto border-r border-background-fg/8 bg-background pb-4 pt-5"
  >
    <div
      class="flex shrink-0 flex-col items-center justify-center px-4"
    >
      <IconBrandLogo class="h-auto w-full" />
      <p
        class="w-full text-right text-xs font-bold text-primary"
        >Web Portal</p
      >
    </div>
    <div class="w-full shrink-0 px-4">
      <p
        class="line-clamp-2 w-full truncate text-lg font-medium text-background-fg/70"
      >
        {{ tenantName }}
      </p>
      <p
        v-if="merchantGroupName"
        class="w-full text-xs font-bold text-primary"
        >{{ merchantGroupName }}</p
      >
    </div>
    <template
      v-if="
        youlendOffer &&
        organizationConfig?.businessFundingEnabled
      "
    >
      <div
        class="border-t border-background-fg/20 px-2 py-4"
      >
        <div class="pb-2">
          <IconAppAdvancePlusBig
            class="text-background-fg"
          />
          <p class="text-sm font-medium">
            You're prequalified for an advance of your
            payments.
          </p>
        </div>

        <RouterLink
          :class="[
            'relative flex h-16 w-full cursor-pointer items-center justify-between rounded-lg bg-primary p-3',
          ]"
          :to="{ name: 'advancePlus' }"
        >
          <div
            class="flex h-full items-center justify-center"
          >
            <IconBusinessFunding
              class="size-8 animate-grow"
            />
          </div>
          <div class="ml-3 flex-1">
            <p
              :class="[
                'line-clamp-2 text-left text-xs font-semibold text-primary-fg',
              ]"
            >
              Grow with <b>Advance+</b> now
            </p>
          </div>
          <div
            class="ml-3 flex h-full items-center justify-center"
          >
            <IconChevronRight
              :class="['size-6 text-primary-fg/50']"
            />
          </div>
        </RouterLink>
      </div>
    </template>
    <div
      class="mt-2 flex grow flex-col border-t border-background-fg/20 pt-4"
    >
      <nav
        class="flex flex-1 flex-col space-y-2 bg-background px-2"
        aria-label="Sidebar"
      >
        <template
          v-for="item in sidebarSections"
          :key="item.name"
        >
          <template v-if="!item.children">
            <RouterLink
              #default="{
                href: childHref,
                navigate: childNavigate,
                route: { meta },
                isExactActive: isChildActive,
              }"
              :to="item"
              custom
            >
              <div
                :class="[
                  'space-y-1 border-l-2',
                  isChildActive
                    ? 'border-primary bg-background-fg/3 text-background-fg/87'
                    : 'border-transparent bg-white text-background-fg/70',
                ]"
              >
                <a
                  :href="childHref"
                  :class="[
                    isChildActive
                      ? 'bg-primary/10'
                      : 'hover:bg-background-fg/3 hover:text-background-fg/87',
                    'group flex w-full items-center rounded-md p-2 text-sm font-medium text-background-fg/70',
                  ]"
                  @click="childNavigate"
                >
                  <component
                    :is="meta.icon"
                    :class="['mr-3 size-6 shrink-0']"
                    aria-hidden="true"
                    @click="childNavigate"
                  />
                  {{ meta.pageTitle }}
                </a>
              </div>
            </RouterLink>
          </template>
          <template v-else>
            <RouterLink
              #default="{ isActive, route: { meta } }"
              custom
              :to="item"
            >
              <div
                :class="[
                  'space-y-1 border-l-2',
                  isActive
                    ? 'border-primary bg-background-fg/3 text-background-fg/87'
                    : 'border-transparent bg-white text-background-fg/70',
                ]"
              >
                <p
                  :class="[
                    'group flex w-full items-center rounded-md py-2 pl-2 pr-1 text-left text-xs font-medium',
                  ]"
                >
                  <span class="flex-1">{{
                    meta.pageTitle
                  }}</span>
                </p>
                <div class="space-y-1">
                  <template
                    v-for="subItem in item.children"
                    :key="subItem.name"
                  >
                    <BaseToolTip
                      :text="getItemToolTip(subItem)"
                      placement="right"
                      :full-width="true"
                    >
                      <RouterLink
                        #default="{
                          href: childHref,
                          navigate: childNavigate,
                          route: { meta: childMeta },
                          isExactActive: isChildActive,
                        }"
                        :to="subItem"
                        custom
                        class="block w-full"
                      >
                        <a
                          :href="childHref"
                          :class="[
                            isChildActive
                              ? 'bg-primary/10'
                              : 'hover:bg-background-fg/3 hover:text-background-fg/87',
                            'group flex w-full items-center rounded-md p-2 text-sm font-medium text-background-fg/70',
                          ]"
                          @click="childNavigate"
                        >
                          <component
                            :is="childMeta.icon"
                            :class="[
                              'mr-3 size-6 shrink-0',
                            ]"
                            aria-hidden="true"
                            @click="childNavigate"
                          />
                          <span class="flex-1">{{
                            childMeta.pageTitle
                          }}</span>
                        </a>
                      </RouterLink>
                    </BaseToolTip>
                  </template>
                </div>
              </div>
            </RouterLink>
          </template>
        </template>
        <div class="flex-1"> </div>

        <template v-if="currentUser">
          <div class="h-px w-full bg-background-fg/20" />
          <div class="space-y-1">
            <div
              class="flex items-center px-4 py-2 pb-0 text-sm font-medium text-background-fg/80"
            >
              {{ currentUser.displayEmail }}
            </div>
            <p
              v-if="currentUser.isShadowUser"
              class="w-full px-4 text-xs font-bold text-primary"
              >{{
                currentUser.isMerchantGroupAdmin
                  ? "Merchant Group Admin"
                  : "Staff User"
              }}</p
            >

            <RouterLink
              v-if="!currentUser?.isShadowUser"
              #default="{ navigate, href }"
              custom
              :to="{ name: 'userPasswordChange' }"
            >
              <div class="relative">
                <a
                  class="block w-full px-4 py-2 text-left text-base font-medium text-background-fg/50 hover:bg-background-fg/10 hover:text-background-fg/80"
                  :href="href"
                  @click="navigate"
                >
                  {{ changePasswordText }}
                </a>
                <template v-if="shouldAlertPassword">
                  <IconExclamationCircleFilled
                    class="absolute right-1 top-1/2 size-4 -translate-y-1/2 text-error"
                  />
                </template>
              </div>
            </RouterLink>
            <button
              class="block w-full px-4 py-2 text-left text-base font-medium text-background-fg/50 hover:bg-background-fg/10 hover:text-background-fg/80"
              @click="onClickLogout"
            >
              Log Out
            </button>
          </div>
        </template>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import { queryClient } from "@/queryClient"
import type { RouteNamedMap } from "@/router/RouteNamedMap"
import type { RouteLocationAsRelative } from "vue-router"

type SidebarSection = RouteLocationAsRelative & {
  children?: Array<RouteLocationAsRelative>
}

const {
  name: tenantName,
  isProductsEnabled,
  isCashiersEnabled,
  hasSalesModule,
  resetTenant,
  youlendOffer,
  merchantGroupName,
  isGratuityEnabled,
  evoConfig,
  acquirer,
} = useTenant()

const { organizationConfig } = useOrganizationConfig()

const reportsSection = computed<SidebarSection>(() => ({
  name: "reportRoot",
  children: [
    { name: "reportsOverview" },
    ...(isGratuityEnabled.value &&
    (acquirer.value !== "evo" ||
      evoConfig.value.gratuityIsAvailable)
      ? [{ name: "reportsGratuity" } as const]
      : []),
    { name: "reportsTransactions" },
    ...(hasSalesModule.value && isProductsEnabled.value
      ? [{ name: "reportsProductSales" } as const]
      : []),
    ...(hasSalesModule.value && isProductsEnabled.value
      ? [{ name: "reportsTopProducts" } as const]
      : []),
  ],
}))

const historySection = computed<SidebarSection>(() => ({
  name: "historyRoot",
  children: [
    ...(hasSalesModule.value
      ? [{ name: "orderIndex" } as const]
      : []),
    { name: "transactionIndex" },
    { name: "zReportIndex" },
  ],
}))

const manageSection = computed<SidebarSection>(() => ({
  name: "manageRoot",
  children: [
    ...(hasSalesModule.value && isProductsEnabled.value
      ? [
          { name: "productIndex" } as const,
          { name: "categoryIndex" } as const,
        ]
      : []),
    ...(hasSalesModule.value && isCashiersEnabled.value
      ? [{ name: "cashierIndex" } as const]
      : []),
    ...(hasSalesModule.value &&
    isProductsEnabled.value &&
    organizationConfig.value?.productImportsEnabled
      ? [{ name: "importIndex" } as const]
      : []),
    { name: "userIndex" },
    { name: "settings" },
  ],
}))

const sidebarSections = computed<Array<SidebarSection>>(
  () => [
    {
      name: "dashboard",
    },
    reportsSection.value,
    historySection.value,
    manageSection.value,
  ]
)

const { currentUser } = useCurrentUser()
const { setToken } = useAuth()

const onClickLogout = async () => {
  setToken(null)
  await nextTick() // Wait for navigation to login page before invalidating queries to prevent refetch from mounted useQuery hooks
  queryClient.invalidateQueries()
  resetTenant()
}

const shouldAlertPassword = computed(
  () => currentUser.value?.hasPassword === false
)
const changePasswordText = computed(() =>
  currentUser.value?.hasPassword
    ? "Change Password"
    : "Set Password"
)

const toolTips: Partial<
  Record<keyof RouteNamedMap, string>
> = {
  reportsOverview: "Quickly access key sales metrics",
  reportsGratuity: "In-depth report on sales with gratuity",
  reportsTransactions:
    "Identify trends in successful transactions over time",
  reportsProductSales:
    "Identify trends in product sales over time",
  reportsTopProducts:
    "Top performing products by count or revenue",
}

const getItemToolTip = (
  item: RouteLocationAsRelative
): string | undefined => {
  return item.name ? toolTips[item.name] : undefined
}
</script>
